import { ChannelId } from '@/Enums/ChannelId.enum';
import type {
  IForecasterAudienceBlock,
  ISavedAudienceBlock,
  ISavedAudienceBlockMeta,
} from '@/Interfaces/AudienceTemplates/AudienceBlock.interface';
import type {
  IAdswizzAudienceMeta,
  IAdswizzAudienceMetaRecord,
  IAudienceChannelReference,
  IAudienceControlOptionsByChannel,
  IAudienceMeta,
  IAudienceMetaRecord,
  IAudienceTargeting,
  IBaseAudienceTargeting,
} from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import type { ILineItemAudienceTargeting } from '@/Interfaces/LineItems/AudienceMeta.interface';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import { sortByLabel } from '@/helpers/sort';

export default class AudienceTargetingService {
  public static get defaultAudienceTargeting() {
    const targeting = {
      include: [],
      exclude: [],
    };
    return { ...targeting };
  }

  public static get defaultLineItemAudienceTargeting(): ILineItemAudienceTargeting {
    const targetingMeta = {
      [ChannelId.Display]: this.defaultAudienceTargeting,
      [ChannelId.CTV]: this.defaultAudienceTargeting,
      [ChannelId.OLV]: this.defaultAudienceTargeting,
      [ChannelId.Audio]: this.defaultAudienceTargeting,
    };
    return { ...targetingMeta };
  }

  public static checkAudiencesInTargeting<T>(audienceTargeting: IBaseAudienceTargeting<T> | undefined): boolean {
    if (!audienceTargeting || !Object.keys(audienceTargeting).length) {
      return false;
    }
    return !Object.values(audienceTargeting).every((arr: T[]) => !(arr ?? []).flat().length);
  }

  public static audienceToControlOption(audience: IAudienceMetaRecord): IAudienceControlOption<number> {
    return {
      value: audience.id,
      label: audience.name,
      groups: audience.groups ?? [],
      excludesGroup: audience.excludes_group,
    };
  }

  public static audiencesToControlOptions(audiences: IAudienceMetaRecord[]): IAudienceControlOption<number>[] {
    return (audiences ?? []).map(audience => this.audienceToControlOption(audience));
  }

  public static adswizzAudienceToControlOption(audience: IAdswizzAudienceMetaRecord): IAudienceControlOption<string> {
    return {
      value: audience.id,
      label: audience.label,
      groups: audience.groups ?? [],
      excludesGroup: audience.excludes_group,
    };
  }

  public static adswizzAudiencesToControlOptions(
    audiences: IAdswizzAudienceMetaRecord[],
  ): IAudienceControlOption<string>[] {
    return (audiences ?? []).map(audience => this.adswizzAudienceToControlOption(audience));
  }

  public static audienceControlOptionsToTargeting<V>(
    controlOptionTargeting: IBaseAudienceTargeting<IAudienceControlOption<V>> | undefined,
  ): IBaseAudienceTargeting<V> {
    if (!controlOptionTargeting) {
      return this.defaultAudienceTargeting;
    }
    return {
      include: (controlOptionTargeting.include ?? [])
        .filter(group => group.length)
        .map(getControlOptionsValues)
        .filter(orGroup => orGroup?.length),
      exclude: getControlOptionsValues(controlOptionTargeting.exclude ?? []),
    };
  }

  public static blocksToRequest(blocks: IForecasterAudienceBlock[][] | undefined): IBaseAudienceTargeting<number>[][] {
    return (blocks ?? [])
      .map(row => row.map(block => this.audienceControlOptionsToTargeting(block.targeting)))
      .filter(row => row.length);
  }

  public static blocksToSave(blocks: IForecasterAudienceBlock[][] | undefined): ISavedAudienceBlock[][] {
    return (blocks ?? [])
      .map(row =>
        row.map(block => ({ label: block.label, ...this.audienceControlOptionsToTargeting(block.targeting) })),
      )
      .filter(row => row.length);
  }

  public static blocksMetaToBlocks(
    blocksMeta: ISavedAudienceBlockMeta[][] | undefined | null,
  ): IForecasterAudienceBlock[][] {
    return (blocksMeta ?? []).map((row, rowIndex) =>
      row.map((block, blockIndex) => ({
        id: `saved-${rowIndex}-${blockIndex}`,
        label: block.label,
        targeting: this.audienceMetaTargetingToAudienceControlOptionTargeting(block),
      })),
    );
  }

  //#region line items
  public static getChannelAudienceTargeting(
    channelAudienceTargeting:
      | Partial<Record<ChannelId, IBaseAudienceTargeting<IAudienceControlOption<number>> | undefined>>
      | undefined
      | null,
    channelId: ChannelId | number | null | undefined,
  ): IAudienceTargeting {
    if (channelId == null) {
      return this.defaultAudienceTargeting;
    }
    const audienceTargeting = channelAudienceTargeting?.[channelId as ChannelId];
    if (!audienceTargeting) {
      return this.defaultAudienceTargeting;
    }

    return this.audienceControlOptionsToTargeting(audienceTargeting);
  }

  public static mapAudiencesByChannelToControlOptions(
    audiencesByChannel: IAudienceChannelReference<IAudienceMetaRecord>[],
  ): IAudienceControlOptionsByChannel<number> {
    return Object.fromEntries(
      audiencesByChannel.map(channel => [channel.name, sortByLabel(this.audiencesToControlOptions(channel.audiences))]),
    ) as IAudienceControlOptionsByChannel<number>;
  }
  //#endregion

  public static getAudienceIdSetFromTargetingControlOptions<T>(
    audienceTargeting: IBaseAudienceTargeting<IAudienceControlOption<T>> | undefined | null,
    key?: keyof IBaseAudienceTargeting<IAudienceControlOption<T>>,
  ): Set<T> {
    if (!audienceTargeting) {
      return new Set();
    }
    if (key) {
      return new Set(getControlOptionsValues([...(audienceTargeting[key] ?? [])].flat())) as Set<T>;
    }
    return new Set(
      getControlOptionsValues([...(audienceTargeting?.include ?? []), ...(audienceTargeting?.exclude ?? [])].flat()),
    ) as Set<T>;
  }

  public static audienceMetaTargetingToAudienceControlOptionTargeting(
    audienceMetaTargeting: IAudienceMeta | undefined,
  ): IBaseAudienceTargeting<IAudienceControlOption<number>> {
    if (!audienceMetaTargeting) {
      return this.defaultAudienceTargeting;
    }
    return {
      include: (audienceMetaTargeting?.include ?? [])
        .map(orGroup => this.audiencesToControlOptions(orGroup))
        .filter(orGroup => orGroup?.length),
      exclude: this.audiencesToControlOptions(audienceMetaTargeting?.exclude ?? []),
    };
  }

  public static adswizzAudienceMetaTargetingToAudienceControlOptionTargeting(
    audienceMetaTargeting: IAdswizzAudienceMeta,
  ): IBaseAudienceTargeting<IAudienceControlOption<string>> {
    if (!audienceMetaTargeting) {
      return this.defaultAudienceTargeting;
    }
    return {
      include: (audienceMetaTargeting?.include ?? [])
        .map(orGroup => this.adswizzAudiencesToControlOptions(orGroup))
        .filter(orGroup => orGroup?.length),
      exclude: this.adswizzAudiencesToControlOptions(audienceMetaTargeting?.exclude ?? []),
    };
  }
}
